import { Alert, Snackbar, Typography, useTheme } from "@mui/material";
import { useMediaQuery, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { Stack } from "@mui/system";
import i18n from "app/i18n";
import MainCard from "components/MainCard";
import { EmptyTable, HeaderSort, TablePagination } from "components/third-party/ReactTable";
import { IPreprocessedFileInfo } from "models/IPreprocessedFileInfo";
import moment from "moment";
import React, { useState } from "react";
import { useMemo, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { HeaderGroup, useTable, useGlobalFilter, useFilters, useSortBy, useExpanded, usePagination, useRowSelect, Row, Cell, Column } from "react-table";
import { renderFilterTypes, GlobalFilter } from "utils/react-table";

interface Props {
    preprocessedFilesInfo: IPreprocessedFileInfo[]
}

export function SessionsTable({ preprocessedFilesInfo }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));

  const filterTypes = useMemo(() => renderFilterTypes, []);
  const sortBy = { id: 'creationDate', desc: true };

  const columns = useMemo(
    () => [
      {
        Header: t('name', 'Name'),
        accessor: 'blobName',
      },
      {
        Header: t('creation_date', 'Creation date'),
        accessor: 'creationDate',
        Cell: ({ value }: { value: Date }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography>
              {moment(value).locale(i18n.language).format('l LTS')}
            </Typography>
          </Stack>
        ),
      },
    ],
    [t]
  );

  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>();
  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowSnackbar(false);
    setSnackbarMessage(null)
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    gotoPage,
    setPageSize,
    state: { globalFilter, pageIndex, pageSize },
    preGlobalFilteredRows,
    setGlobalFilter
  } = useTable(
    {
      columns: columns as unknown as Column[],
      data: preprocessedFilesInfo,
      autoResetPage: false,
      autoResetFilters: false,
      filterTypes,
      initialState: { pageIndex: 0, pageSize: 10, hiddenColumns: [], sortBy: [sortBy] }
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  return (
    <>
      <MainCard content={false}>
        <Stack spacing={3}>
          <Stack
            direction={matchDownSM ? 'column' : 'row'}
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 3, pb: 0 }} >
            <GlobalFilter
                preGlobalFilteredRows={preGlobalFilteredRows}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                size="small"
            />
          </Stack>
          <Table {...getTableProps()}>
            <TableHead>
              {headerGroups.map((headerGroup: HeaderGroup<{}>) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: HeaderGroup) => (
                  <TableCell {...column.getHeaderProps([{ className: column.className }, column.getSortByToggleProps()])}>
                    <HeaderSort column={column} />
                  </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {
                page.length > 0 ? (
                  page.map((row: Row, i: number) => {
                    prepareRow(row);
      
                    return (
                      <Fragment key={i}>
                        <TableRow
                          {...row.getRowProps()}
                          sx={{ cursor: 'pointer', bgcolor: 'inherit' }} >
                            {row.cells.map((cell: Cell) => (
                            <TableCell {...cell.getCellProps([{ className: cell.column.className }])}>{cell.render('Cell')}</TableCell>
                          ))}
                      </TableRow>
                      {row.isExpanded}
                    </Fragment>
                  );
                })
              ) : (
                <EmptyTable msg={t('no_data_found', 'Data not found')} colSpan={7} />
              )
            }
            <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
            <TableCell sx={{ p: 2, py: 3 }} colSpan={9}>
              <TablePagination gotoPage={gotoPage} rows={rows} setPageSize={setPageSize} pageSize={pageSize} pageIndex={pageIndex} />
            </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Stack>

      <Snackbar open={showSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center'}}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>{snackbarMessage}</Alert>
      </Snackbar>
      </MainCard>
    </>
  );
}